import path from 'path';
import fs from 'fs-extra';
import { logger } from '../utils/logger';
import { z } from 'zod';
import { v4 as uuidv4 } from 'uuid';
import { EventEmitter } from 'events';

// Team member schema
export const teamMemberSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  role: z.enum(['owner', 'admin', 'member', 'viewer']),
  avatar: z.string().optional(),
  skills: z.array(z.string()).default([]),
  availability: z.enum(['available', 'busy', 'away', 'offline']).default('available'),
  timezone: z.string().default('UTC'),
  joinedAt: z.string().datetime(),
  lastActiveAt: z.string().datetime().optional()
});

// Team schema
export const teamSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  members: z.array(teamMemberSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

// Team settings schema
export const teamSettingsSchema = z.object({
  syncEnabled: z.boolean().default(true),
  notificationsEnabled: z.boolean().default(true),
  autoAssignEnabled: z.boolean().default(false),
  workingHours: z.object({
    start: z.string().default('09:00'),
    end: z.string().default('18:00'),
    timezone: z.string().default('UTC')
  }).optional(),
  taskAssignmentStrategy: z.enum(['round-robin', 'load-balanced', 'skill-based', 'manual']).default('manual')
});

export type TeamMember = z.infer<typeof teamMemberSchema>;
export type Team = z.infer<typeof teamSchema>;
export type TeamSettings = z.infer<typeof teamSettingsSchema>;

export interface TeamManagerOptions {
  projectRoot?: string;
  teamConfigPath?: string;
}

export interface TaskAssignment {
  taskId: string;
  assigneeId: string;
  assignedBy: string;
  assignedAt: string;
  reason?: string;
}

export interface TeamActivity {
  id: string;
  type: 'task_assigned' | 'task_completed' | 'member_joined' | 'member_left' | 'status_changed';
  actorId: string;
  targetId?: string;
  data?: any;
  timestamp: string;
}

export class TeamManager extends EventEmitter {
  private projectRoot: string;
  private teamConfigPath: string;
  private team: Team | null = null;
  private settings: TeamSettings | null = null;
  private currentUserId: string | null = null;

  constructor(options: TeamManagerOptions = {}) {
    super();
    this.projectRoot = options.projectRoot || process.cwd();
    this.teamConfigPath = options.teamConfigPath || path.join(this.projectRoot, '.workflow/team/config.json');
  }

  async initialize(): Promise<void> {
    await fs.ensureDir(path.dirname(this.teamConfigPath));
    
    // Load existing team config or create new
    if (await fs.pathExists(this.teamConfigPath)) {
      await this.loadTeam();
    } else {
      await this.createTeam();
    }
  }

  private async loadTeam(): Promise<void> {
    try {
      const data = await fs.readJson(this.teamConfigPath);
      this.team = teamSchema.parse(data.team);
      this.settings = teamSettingsSchema.parse(data.settings || {});
      this.currentUserId = data.currentUserId || null;
    } catch (error) {
      logger.error('Failed to load team configuration:', error);
      throw error;
    }
  }

  private async saveTeam(): Promise<void> {
    if (!this.team) return;
    
    await fs.writeJson(this.teamConfigPath, {
      team: this.team,
      settings: this.settings,
      currentUserId: this.currentUserId
    }, { spaces: 2 });
  }

  async createTeam(name?: string): Promise<Team> {
    const teamName = name || path.basename(this.projectRoot) + ' Team';
    
    this.team = {
      id: uuidv4(),
      name: teamName,
      description: `Team workspace for ${path.basename(this.projectRoot)}`,
      members: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    this.settings = teamSettingsSchema.parse({});
    
    await this.saveTeam();
    return this.team;
  }

  async addMember(member: Omit<TeamMember, 'id' | 'joinedAt'>): Promise<TeamMember> {
    if (!this.team) {
      throw new Error('Team not initialized');
    }
    
    const newMember: TeamMember = {
      ...member,
      id: uuidv4(),
      joinedAt: new Date().toISOString()
    };
    
    this.team.members.push(newMember);
    this.team.updatedAt = new Date().toISOString();
    
    await this.saveTeam();
    
    // Emit event
    this.emit('memberJoined', newMember);
    
    return newMember;
  }

  async removeMember(memberId: string): Promise<void> {
    if (!this.team) {
      throw new Error('Team not initialized');
    }
    
    const memberIndex = this.team.members.findIndex(m => m.id === memberId);
    if (memberIndex === -1) {
      throw new Error('Member not found');
    }
    
    const removedMember = this.team.members[memberIndex];
    this.team.members.splice(memberIndex, 1);
    this.team.updatedAt = new Date().toISOString();
    
    await this.saveTeam();
    
    // Emit event
    this.emit('memberLeft', removedMember);
  }

  async updateMemberStatus(memberId: string, availability: TeamMember['availability']): Promise<void> {
    if (!this.team) {
      throw new Error('Team not initialized');
    }
    
    const member = this.team.members.find(m => m.id === memberId);
    if (!member) {
      throw new Error('Member not found');
    }
    
    member.availability = availability;
    member.lastActiveAt = new Date().toISOString();
    
    await this.saveTeam();
    
    // Emit event
    this.emit('statusChanged', { member, availability });
  }

  async setCurrentUser(memberId: string): Promise<void> {
    if (!this.team) {
      throw new Error('Team not initialized');
    }
    
    const member = this.team.members.find(m => m.id === memberId);
    if (!member) {
      throw new Error('Member not found');
    }
    
    this.currentUserId = memberId;
    await this.saveTeam();
  }

  getCurrentUser(): TeamMember | null {
    if (!this.team || !this.currentUserId) {
      return null;
    }
    
    return this.team.members.find(m => m.id === this.currentUserId) || null;
  }

  getTeam(): Team | null {
    return this.team;
  }

  getMembers(): TeamMember[] {
    return this.team?.members || [];
  }

  getMemberById(memberId: string): TeamMember | null {
    if (!this.team) return null;
    return this.team.members.find(m => m.id === memberId) || null;
  }

  getAvailableMembers(): TeamMember[] {
    if (!this.team) return [];
    return this.team.members.filter(m => m.availability === 'available');
  }

  getMembersBySkill(skill: string): TeamMember[] {
    if (!this.team) return [];
    return this.team.members.filter(m => m.skills.includes(skill));
  }

  async assignTask(taskId: string, assigneeId: string, assignedBy?: string): Promise<TaskAssignment> {
    if (!this.team) {
      throw new Error('Team not initialized');
    }
    
    const assignee = this.getMemberById(assigneeId);
    if (!assignee) {
      throw new Error('Assignee not found');
    }
    
    const assignment: TaskAssignment = {
      taskId,
      assigneeId,
      assignedBy: assignedBy || this.currentUserId || 'system',
      assignedAt: new Date().toISOString()
    };
    
    // Update member's last active time
    assignee.lastActiveAt = new Date().toISOString();
    await this.saveTeam();
    
    // Emit event
    this.emit('taskAssigned', assignment);
    
    return assignment;
  }

  async suggestAssignee(taskType: string, skills?: string[]): Promise<TeamMember | null> {
    if (!this.team || !this.settings) return null;
    
    const availableMembers = this.getAvailableMembers();
    if (availableMembers.length === 0) return null;
    
    switch (this.settings.taskAssignmentStrategy) {
      case 'skill-based':
        if (skills && skills.length > 0) {
          // Find member with most matching skills
          const membersWithScores = availableMembers.map(member => {
            const matchingSkills = skills.filter(skill => member.skills.includes(skill));
            return { member, score: matchingSkills.length };
          });
          
          membersWithScores.sort((a, b) => b.score - a.score);
          if (membersWithScores[0].score > 0) {
            return membersWithScores[0].member;
          }
        }
        // Fall through to round-robin if no skills match
        
      case 'round-robin':
        // Simple round-robin based on last active time
        const sortedByActivity = [...availableMembers].sort((a, b) => {
          const aTime = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
          const bTime = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0;
          return aTime - bTime;
        });
        return sortedByActivity[0];
        
      case 'load-balanced':
        // Would need task count tracking, for now use round-robin
        return this.suggestAssignee(taskType, skills);
        
      case 'manual':
      default:
        return null;
    }
  }

  async updateSettings(settings: Partial<TeamSettings>): Promise<void> {
    this.settings = { ...this.settings, ...settings };
    await this.saveTeam();
  }

  getSettings(): TeamSettings | null {
    return this.settings;
  }

  // Activity tracking
  async logActivity(activity: Omit<TeamActivity, 'id' | 'timestamp'>): Promise<void> {
    const fullActivity: TeamActivity = {
      ...activity,
      id: uuidv4(),
      timestamp: new Date().toISOString()
    };
    
    // Save to activity log file
    const activityPath = path.join(path.dirname(this.teamConfigPath), 'activity.jsonl');
    await fs.appendFile(activityPath, JSON.stringify(fullActivity) + '\n');
    
    // Emit event
    this.emit('activity', fullActivity);
  }

  async getRecentActivity(limit = 50): Promise<TeamActivity[]> {
    const activityPath = path.join(path.dirname(this.teamConfigPath), 'activity.jsonl');
    
    if (!await fs.pathExists(activityPath)) {
      return [];
    }
    
    const content = await fs.readFile(activityPath, 'utf-8');
    const lines = content.trim().split('\n').filter(line => line);
    
    const activities = lines
      .map(line => {
        try {
          return JSON.parse(line) as TeamActivity;
        } catch {
          return null;
        }
      })
      .filter((activity): activity is TeamActivity => activity !== null);
    
    // Return the most recent activities
    return activities.slice(-limit).reverse();
  }
}